import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Settings,
  Users,
  Image,
  Loader2,
} from 'lucide-react';
import type { WizardFormData, WizardMode } from '../types';
import type { SchoolOptions } from '@/types/school';

interface SummaryStepProps {
  formData: WizardFormData;
  options: SchoolOptions | null;
  isCreating?: boolean;
  mode?: WizardMode;
}

export function SummaryStep({
  formData,
  options,
  isCreating = false,
  mode = 'create',
}: SummaryStepProps) {
  const isUpgrade = mode === 'upgradeV2';
  const getTypeLabel = (type: string) => {
    return options?.types.find((t) => t.value === type)?.label || type;
  };

  const getSupportLabel = (support: string) => {
    return options?.supports.find((s) => s.value === support)?.label || support;
  };

  const getUserName = (id: number | null) => {
    if (!id) return null;
    return options?.users.find((u) => u.id === id)?.fullname;
  };

  const getWhiteLabelName = (id: number | null) => {
    if (!id) return null;
    return options?.whiteLabels.find((wl) => wl.id === id)?.name;
  };

  // Loading state during school creation
  if (isCreating) {
    return (
      <div className="space-y-6 max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-foreground">
            {isUpgrade ? 'Enregistrement en cours...' : 'Creation en cours...'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isUpgrade ? (
              <>Veuillez patienter pendant la mise à jour de l'école <strong>{formData.name}</strong></>
            ) : (
              <>Veuillez patienter pendant la création de l'école <strong>{formData.name}</strong></>
            )}
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-muted-foreground">
                {isUpgrade ? "Mise à jour de l'école..." : "Création de l'école..."}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Summary view
  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">Résumé</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isUpgrade
            ? "Vérifiez les informations avant d'enregistrer et de passer au déploiement V2"
            : "Vérifiez les informations avant de créer l'école"}
        </p>
      </div>

      <div className="grid gap-4">
        {/* Informations */}
        <Card>
          <CardHeader className="py-3 mb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Building2 className="h-4 w-4 text-primary" />
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Code:</span>
                <span className="ml-2 font-medium">{formData.code || '-'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Nom:</span>
                <span className="ml-2 font-medium">{formData.name || '-'}</span>
              </div>
              {formData.email && (
                <div className="flex items-center gap-1">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <span>{formData.email}</span>
                </div>
              )}
              {formData.phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  <span>{formData.phone}</span>
                </div>
              )}
              {formData.address && (
                <div className="flex items-start gap-1 col-span-2">
                  <MapPin className="h-3 w-3 text-muted-foreground mt-1" />
                  <span>{formData.address}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Email expediteur:</span>
                <span className="ml-2">{formData.emailSender || '-'}</span>
              </div>
              {formData.isEducationNationale && (
                <>
                  <div>
                    <span className="text-muted-foreground">RNE (établissement principal):</span>
                    <span className="ml-2 font-mono">{formData.rne || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Académie:</span>
                    <span className="ml-2">{formData.codeAcademie || '-'}</span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card>
          <CardHeader className="py-3 mb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Settings className="h-4 w-4 text-primary" />
              Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Type:</span>
                <span className="ml-2">{getTypeLabel(formData.type)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Support:</span>
                <span className="ml-2">{getSupportLabel(formData.support)}</span>
              </div>
              <div className="flex gap-2">
                {formData.vip && <Badge variant="success">VIP</Badge>}

              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contacts */}
        {(formData.retailerId ||
          formData.technicalContactId ||
          formData.salesContactId) && (
          <Card>
            <CardHeader className="py-3 mb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4 text-primary" />
                Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {formData.retailerId && (
                  <div>
                    <span className="text-muted-foreground">Apporteur:</span>
                    <span className="ml-2">
                      {getUserName(formData.retailerId)}
                    </span>
                  </div>
                )}
                {formData.technicalContactId && (
                  <div>
                    <span className="text-muted-foreground">
                      Contact technique:
                    </span>
                    <span className="ml-2">
                      {getUserName(formData.technicalContactId)}
                    </span>
                  </div>
                )}
                {formData.salesContactId && (
                  <div>
                    <span className="text-muted-foreground">
                      Contact commercial:
                    </span>
                    <span className="ml-2">
                      {getUserName(formData.salesContactId)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}


        {/* Branding */}
        {(formData.whiteLabelEntityId ||
          formData.logoFile ||
          formData.faviconFile) && (
          <Card>
            <CardHeader className="py-3 mb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Image className="h-4 w-4 text-primary" />
                Branding
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {formData.whiteLabelEntityId && (
                  <div>
                    <span className="text-muted-foreground">Marque blanche :</span>
                    <span className="ml-2">
                      {getWhiteLabelName(formData.whiteLabelEntityId)}
                    </span>
                  </div>
                )}
                <div className="col-span-2 flex gap-2">
                  {formData.logoFile && (
                    <Badge variant="outline">Logo</Badge>
                  )}
                  {formData.logoSigninFile && (
                    <Badge variant="outline">Logo connexion</Badge>
                  )}
                  {formData.faviconFile && (
                    <Badge variant="outline">Favicon</Badge>
                  )}
                  {formData.coverFile && (
                    <Badge variant="outline">Couverture</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Établissements secondaires saisis dans le wizard (mode création) */}
        {formData.establishments.length > 0 && (
          <Card>
            <CardHeader className="py-3 mb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Building2 className="h-4 w-4 text-primary" />
                Établissements
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">Principal :</span>
                  <span className="ml-2 font-medium">{formData.name}</span>
                  {formData.isEducationNationale && formData.rne && (
                    <span className="ml-2 font-mono text-muted-foreground">{formData.rne}</span>
                  )}
                </div>
                {formData.establishments.map((establishment) => (
                  <div key={establishment.localId}>
                    <span className="text-muted-foreground">Secondaire :</span>
                    <span className="ml-2">{establishment.name}</span>
                    {formData.isEducationNationale && establishment.rne && (
                      <span className="ml-2 font-mono text-muted-foreground">{establishment.rne}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Note about deployment */}
        <div className="text-center text-sm text-muted-foreground mt-4">
          <p>
            {isUpgrade
              ? "Les informations de l'école seront mises à jour, puis vous pourrez lancer le déploiement V2."
              : "L'école sera créée sans déploiement. Vous pourrez déployer sur V1 et/ou V2 à l'étape suivante."}
          </p>
        </div>
      </div>
    </div>
  );
}
